import { useRef, useState } from "react";
import { UserPlus, Mail, Shield, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useFieldErrors } from "@/components/Tools/use-field-errors";
import {
  useWorkspaceMembers,
  useWorkspaceInvitations,
  useInviteMember,
  useUpdateMemberRole,
  useRemoveMember,
  useRevokeInvitation,
  useCurrentUserRole,
} from "@/hooks/use-workspace-members";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspaces } from "@/hooks/use-workspaces";
import type { MemberRole } from "@/services/team.service";
import { inviteEmailError } from "@/utils/workspace-rules";

const ROLE_DESCRIPTIONS: Record<string, string> = {
  owner: "Full control over workspace",
  admin: "Manage members, API keys, and settings",
  developer: "Create and edit agents, phone numbers",
  member: "View workspace data",
};

const ROLE_BADGES: Record<string, "default" | "secondary"> = { owner: "default", admin: "secondary" };

export function TeamMembersTab() {
  const { currentWorkspaceId } = useWorkspace();
  const { data: workspaces } = useWorkspaces();
  const { data: currentRole } = useCurrentUserRole(currentWorkspaceId);
  const canManageMembers = currentRole === "owner" || currentRole === "admin";
  // The roster and the invitations are an owner's or admin's to read; anyone else
  // would only be refused, so they are not asked for.
  const teamWorkspaceId = canManageMembers ? currentWorkspaceId : undefined;
  const { data: membersResult } = useWorkspaceMembers(teamWorkspaceId);
  const members = membersResult?.members;
  const ownerEmail = membersResult?.ownerEmail;
  const { data: invitations } = useWorkspaceInvitations(teamWorkspaceId);
  const inviteMember = useInviteMember();
  const updateMemberRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  const revokeInvitation = useRevokeInvitation();

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<MemberRole>("member");
  const inviteRef = useRef<HTMLDivElement>(null);
  const { field, error, check, reset } = useFieldErrors<"email">({ email: inviteEmailError(inviteEmail) });

  const currentWorkspace = workspaces?.find(w => w.id === currentWorkspaceId);

  // `mutate`, not `mutateAsync`: a refusal is the hook's toast, not an uncaught rejection, and
  // the dialog stays open with the address in it so it can be corrected.
  const handleInvite = () => {
    // An address the server would refuse is said under the field and never sent.
    if (!check(inviteRef.current)) return;
    inviteMember.mutate(
      { workspaceId: currentWorkspaceId!, email: inviteEmail, role: inviteRole },
      {
        onSuccess: () => {
          setInviteEmail("");
          setInviteRole("member");
          // The next invitation's address is not judged before its own Send.
          reset();
          setShowInviteDialog(false);
        },
      },
    );
  };

  if (!canManageMembers) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Team Members</CardTitle>
          <CardDescription>You don't have permission to manage team members</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Team Members</CardTitle>
              <CardDescription>
                Invite and manage members for {currentWorkspace?.name}
              </CardDescription>
            </div>
            <Button onClick={() => setShowInviteDialog(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Show workspace owner */}
              {currentWorkspace && (
                <TableRow>
                  <TableCell className="font-medium">
                    {ownerEmail || "Owner"}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>
                    <Badge variant="default">Owner</Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(currentWorkspace.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">-</TableCell>
                </TableRow>
              )}
              
              {/* Show members */}
              {members?.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">
                    {member.email || "Unknown"}
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>
                    {currentRole === "owner" ? (
                      <Select
                        value={member.role}
                        onValueChange={(value) =>
                          updateMemberRole.mutate({
                            memberId: member.id,
                            role: value as MemberRole,
                            workspaceId: currentWorkspaceId!,
                          })
                        }
                      >
                        <SelectTrigger className="w-[130px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="developer">Developer</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={ROLE_BADGES[member.role] ?? "outline"}>
                        {member.role}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(member.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${member.email || "member"}`}
                      // One removal at a time: a second press would find the member gone (404).
                      disabled={removeMember.isPending}
                      onClick={() =>
                        removeMember.mutate({
                          memberId: member.id,
                          workspaceId: currentWorkspaceId!,
                        })
                      }
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {invitations && invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Invitations</CardTitle>
            <CardDescription>Invitations waiting to be accepted</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{invitation.email}</p>
                      <div className="text-sm text-muted-foreground">
                        Invited as <Badge variant="outline" className="ml-1">{invitation.role}</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Expires {new Date(invitation.expires_at).toLocaleDateString()}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Revoke the invitation to ${invitation.email}`}
                      // One revoke at a time: a second press would find it revoked (404).
                      disabled={revokeInvitation.isPending}
                      onClick={() =>
                        revokeInvitation.mutate({
                          invitationId: invitation.id,
                          workspaceId: currentWorkspaceId!,
                        })
                      }
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role Descriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Role Permissions</CardTitle>
          <CardDescription>What each role can do</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {["owner", "admin", "developer", "member"].map((role) => (
              <div key={role} className="flex items-start gap-3 p-3 rounded-lg border border-border">
                <Shield className="w-5 h-5 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="font-medium capitalize">{role}</p>
                  <p className="text-sm text-muted-foreground">{ROLE_DESCRIPTIONS[role]}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent ref={inviteRef} className="[&_[aria-invalid=true]]:border-destructive">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join {currentWorkspace?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="teammate@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                {...field("email", "invite-email")}
              />
              {error("email", "invite-email")}
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as MemberRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="developer">Developer</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {ROLE_DESCRIPTIONS[inviteRole]}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviteMember.isPending || !inviteEmail.trim()}>
              {inviteMember.isPending ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
